import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch, FaFilter } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { fetchPromoCodes, deletePromoCode, clearError, clearSuccess } from '../../../redux/Slices/AdminPromoCodesSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';

const AdminPromoCodes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();

  const { promoCodes, pagination, loading, error, success } = useSelector(state => state.adminPromoCodes);

  const [filters, setFilters] = useState({
    page: 1,
    from: '',
    to: '',
    code: '',
    status: '',
    created_by_type: ''
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
    data: null
  });

  useEffect(() => {
    setTitle('البرومو كود');
    dispatch(fetchPromoCodes(filters));
  }, [dispatch, filters.page, filters.from, filters.to, filters.code, filters.status, filters.created_by_type]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearSuccess());
      dispatch(fetchPromoCodes(filters));
    }
  }, [success, dispatch, filters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  const handleSearch = () => {
    dispatch(fetchPromoCodes(filters));
  };

  const handleClearFilters = () => {
    const newFilters = {
      page: 1,
      from: '',
      to: '',
      code: '',
      status: '',
      created_by_type: ''
    };
    setFilters(newFilters);
    dispatch(fetchPromoCodes(newFilters));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleView = (promoCodeId) => {
    navigate(`/admin-promo-codes/${promoCodeId}`);
  };

  const handleEdit = (promoCodeId) => {
    navigate(`/admin-promo-codes/${promoCodeId}/edit`);
  };

  const handleDelete = (promoCode) => {
    setConfirmModal({
      isOpen: true,
      action: 'delete',
      data: promoCode.id,
      code: promoCode.code
    });
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === 'delete') {
      dispatch(deletePromoCode(confirmModal.data));
      setConfirmModal({ isOpen: false, action: null, data: null, code: null });
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = {
      1: { class: 'bg-success', text: 'نشط' },
      0: { class: 'bg-danger', text: 'غير نشط' },
      true: { class: 'bg-success', text: 'نشط' },
      false: { class: 'bg-danger', text: 'غير نشط' }
    };
    
    const currentStatus = statusInfo[status] || { class: 'bg-secondary', text: 'غير محدد' };
    
    return (
      <span className={`badge ${currentStatus.class} text-white`}>
        {currentStatus.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderPagination = () => {
    if (!pagination || pagination.last_page <= 1) return null;

    const pages = [];
    const startPage = Math.max(1, pagination.current_page - 2);
    const endPage = Math.min(pagination.last_page, pagination.current_page + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${i === pagination.current_page ? 'active' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(i)}
            disabled={loading}
          >
            {i}
          </button>
        </li>
      );
    }

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1 || loading}
            >
              السابق
            </button>
          </li>
          {pages}
          <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page || loading}
            >
              التالي
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="main_content">
      
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center  my-4">
        <h5 className="mb-0">البرومو كود</h5>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn d-flex align-items-center gap-2"
          onClick={() => navigate('/admin-promo-codes/add')}
          style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
        >
          <FaPlus />
          إضافة برومو كود
        </motion.button>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card mb-4"
      >
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">كود البرومو</label>
              <input
                type="text"
                className="form-control"
                name="code"
                value={filters.code}
                onChange={handleInputChange}
                placeholder="ابحث بالكود"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">من تاريخ</label>
              <input
                type="date"
                className="form-control"
                name="from"
                value={filters.from}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">إلى تاريخ</label>
              <input
                type="date"
                className="form-control"
                name="to"
                value={filters.to}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">الحالة</label>
              <select
                className="form-select"
                name="status"
                value={filters.status}
                onChange={handleInputChange}
              >
                <option value="">جميع الحالات</option>
                <option value="1">نشط</option>
                <option value="0">غير نشط</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">نوع المنشئ</label>
              <select
                className="form-select"
                name="created_by_type"
                value={filters.created_by_type}
                onChange={handleInputChange}
              >
                <option value="">الكل</option>
                <option value="admin">Admin</option>
                <option value="store">Store</option>
                <option value="pharmacy">Pharmacy</option>
              </select>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12 d-flex gap-2 justify-content-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn d-flex align-items-center justify-content-center gap-2"
                onClick={handleSearch}
                disabled={loading}
                style={{ minWidth: '120px', backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
              >
                <FaSearch />
                بحث
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={handleClearFilters}
                disabled={loading}
                style={{ minWidth: '120px' }}
              >
                <FaFilter />
                مسح الفلاتر
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : !promoCodes || !Array.isArray(promoCodes) || promoCodes.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">لا توجد برومو كود</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>الكود</th>
                      <th>نسبة الخصم</th>
                      <th>الحد الأدنى للطلب</th>
                      <th>حد الاستخدام</th>
                      <th>المستخدم</th>
                      <th>تاريخ البداية</th>
                      <th>تاريخ الانتهاء</th>
                      <th>الحالة</th>
                      <th>نوع المنشئ</th>
                      <th>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((promoCode, index) => (
                      <tr key={promoCode.id}>
                        <td>{(filters.page - 1) * 10 + index + 1}</td>
                        <td>
                          <span className="fw-bold text-primary">
                            {promoCode.code}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info text-white">
                            {Math.round(promoCode.discount_percent)}%
                          </span>
                        </td>
                        <td>{promoCode.min_order || 0} جنيه</td>
                        <td>{promoCode.usage_limit || 'غير محدود'}</td>
                        <td>{promoCode.used_count || 0}</td>
                        <td>{formatDate(promoCode.starts_at)}</td>
                        <td>{formatDate(promoCode.expires_at)}</td>
                        <td>{getStatusBadge(promoCode.status)}</td>
                        <td>
                          <span className="badge bg-secondary text-white">
                            {promoCode.created_by_type || 'غير محدد'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="btn btn-sm btn-primary"
                              onClick={() => handleView(promoCode.id)}
                              title="عرض"
                            >
                              <FaEye />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="btn btn-sm"
                              onClick={() => handleEdit(promoCode.id)}
                              title="تعديل"
                              style={{ backgroundColor: '#17a2b8', color: 'white', border: 'none' }}
                            >
                              <FaEdit />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="btn btn-sm"
                              onClick={() => handleDelete(promoCode)}
                              title="حذف"
                              style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}
                            >
                              <FaTrash />
                            </motion.button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {renderPagination()}
            </>
          )}
        </div>
      </motion.div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, data: null, code: null })}
        onConfirm={handleConfirmAction}
        title="تأكيد الحذف"
        message={`هل أنت متأكد من حذف البرومو كود "${confirmModal.code}"؟`}
        confirmText="حذف"
        cancelText="إلغاء"
        type="danger"
      />
    </div>
  );
};

export default AdminPromoCodes;

